import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Allroutes from '../src/routes';
import {loginSuccess} from './store/Slices/userSlice';
import {RootState} from './store/configstore';
import Toast from './package/Toaster';
import {toast_msg} from './store/Slices/commonSlice';
import DialogApiStatus from './components/dialogs/DialogApiStatus';
import {apiGet, SESSION_EXPIRED_EVENT} from './axiosConfig';
import {MusicPlayAssignCtx} from './pages/music-section/MusicSectionContexts';
import ModalAriaHiddenFix from './components/common/ModalAriaHiddenFix';
import {locationToRedirectTo, redirectToQuery} from './utils/redirectTo';

const PreLoadUser = ({onComplete}: {onComplete: (val: boolean) => void}) => {
  const dispatch = useDispatch();
  const [apiState, setApiState] = useState({status: '', error: null});
  const [isCompleted, setIsCompleted] = useState(false);
  const navigate = useNavigate();

  const fetchCurrentUser = useCallback(() => {
    setApiState({status: 'pending', error: null});
    if (!isCompleted) {
      const impersonatedUserToken =
        sessionStorage.getItem('access_token') || '';
      //impersonated_user
      apiGet('/v1/user/me', {skipAuthRedirect: true})
        .then(res => {
          if (impersonatedUserToken) {
            dispatch(
              loginSuccess(Object.assign(res.data, {isImpersonatedUser: true})),
            );
          } else {
            dispatch(
              loginSuccess(
                Object.assign(res.data, {
                  token: localStorage.getItem('access_token'),
                }),
              ),
            );
            // To keep the impersonated company
            const companyId = parseInt(
              sessionStorage.getItem('impersonated_company') || '',
              10,
            );

            if (window.location.pathname.startsWith('/partner-impersonate')) {
              // An explicit `/partner-impersonate/:companyId` URL is
              // authoritative: it already names the company to select and,
              // via `?redirectTo=`, the page to land on. Deep links from the
              // Next-Gen Player ("Open in Voyager") arrive in exactly this
              // shape. Re-navigating here rewrote the company from
              // `sessionStorage` and dropped the query string, so the
              // operator lost both the requested partner and the requested
              // page and was left on the dashboard.
              setIsCompleted(true);
            } else if (companyId > 0) {
              // Re-select the company this tab was already impersonating,
              // then return to where the operator actually was.
              const target = locationToRedirectTo(window.location);
              setIsCompleted(true);
              navigate(
                `/partner-impersonate/${companyId}?${redirectToQuery(target)}`,
              );
            }
          }
          setApiState({status: '', error: null});
          setTimeout(onComplete, 10, true);
        })
        .catch(error => {
          const status = error.response?.status;

          // A stored token that is already expired/revoked on page load is not a
          // "session expired" event for the user - treat it as signed out and let
          // the AuthUsers guard route to the sign-in page.
          if (status === 401) {
            localStorage.clear();
            sessionStorage.clear();
            setApiState({status: '', error: null});
            onComplete(true);
            return;
          }

          setApiState(
            status === 503
              ? {status: '', error: null}
              : {status: 'error', error},
          );
        });
    }
  }, [dispatch, isCompleted, navigate, onComplete]);

  useEffect(() => {
    if (
      (sessionStorage.hasOwnProperty('access_token') &&
        sessionStorage.hasOwnProperty('impersonated_user')) ||
      localStorage.hasOwnProperty('access_token')
    ) {
      fetchCurrentUser();
    } else {
      onComplete(true);
    }
  }, [onComplete, fetchCurrentUser, isCompleted]);
  if (apiState.status === '') return null;
  return (
    <DialogApiStatus
      apiState={apiState}
      pendingTitle='Fetching user details'
      pendingSubTitle='Please wait...'
      successTitle=''
      errorTitle='Fetching user details is failed!'
      onRetry={fetchCurrentUser}
    />
  );
};

const SessionExpiredDialog = () => {
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const handleExpired = () => setIsExpired(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, handleExpired);
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleExpired);
  }, []);

  if (!isExpired) return null;
  return (
    <DialogApiStatus
      apiState={{status: 'warning', error: null}}
      pendingTitle=''
      pendingSubTitle=''
      successTitle=''
      errorTitle=''
      warningTitle='Your login session has expired! Redirecting to the login page.'
    />
  );
};

function App() {
  const dispatch = useDispatch();
  const [isInitialised, setInitialised] = useState(false);
  const [trackId, setTrackId] = useState<string | number | null>(null);

  const {toastMsg, toastType} = useSelector((state: RootState) => state.common);
  React.useEffect(() => {
    if (toastMsg !== '') {
      Toast(toastMsg, toastType);
    }
    dispatch(toast_msg({toastMsg: '', toastType: 'error'}));
  }, [toastMsg, toastType, dispatch]);
  return (
    <>
      {isInitialised ? (
        <MusicPlayAssignCtx.Provider
          value={{
            trackId,
            setTrackId,
          }}>
          <ModalAriaHiddenFix />
          <Allroutes />
        </MusicPlayAssignCtx.Provider>
      ) : (
        <PreLoadUser onComplete={setInitialised} />
      )}
      <SessionExpiredDialog />
    </>
  );
}

export default App;
